import { useState } from 'react';
import { DataLoadError } from '@/components/DataLoadError';
import { createFileRoute } from '@tanstack/react-router';
import { OUTBOUND_MAPPINGS } from '@/data/mock';
import { useOutboundMappings } from '@/lib/use-admin-data';
import type { DisputeProcessor } from '@/types/index';
import { PROCESSOR_LABELS } from '@/types/index';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/utils';
import { Plus, Export, PencilSimple, BracketsCurly } from '@phosphor-icons/react';

/* ------------------------------------------------------------------ */
/*  Processor tabs                                                     */
/* ------------------------------------------------------------------ */
const PROCESSORS: { key: DisputeProcessor; label: string }[] = [
  { key: 'justt', label: 'Justt' },
  { key: 'kount_dcm', label: 'Kount DCM' },
  { key: 'midigator', label: 'Midigator' },
];

/* ------------------------------------------------------------------ */
/*  Sample payload per processor (for preview)                         */
/* ------------------------------------------------------------------ */
const SAMPLE_PAYLOADS: Record<DisputeProcessor, Record<string, unknown>> = {
  justt: {
    transaction_id: 'a3f7c2d1-9e4b-4a8c-b6f0-1d2e3f4a5b6c',
    total_amount: 4999,
    currency_code: 'USD',
    customer_email: 'john.doe@example.com',
    card_last_four: '4242',
    subscription_status: 'active',
    transaction_date: '2026-03-14T10:22:00Z',
  },
  kount_dcm: {
    txn_ref: 'TXN-88421',
    amount: 49.99,
    email: 'john.doe@example.com',
  },
  midigator: {
    OrderId: 'TXN-88421',
    Amount: 49.99,
  },
};

/* ------------------------------------------------------------------ */
/*  JSON syntax highlight helper                                       */
/* ------------------------------------------------------------------ */
function SyntaxHighlightedJson({ data }: { data: Record<string, unknown> }) {
  const jsonStr = JSON.stringify(data, null, 2);
  const lines = jsonStr.split('\n');

  return (
    <pre className="text-sm font-mono leading-relaxed">
      {lines.map((line, i) => {
        // Match key-value patterns
        const kvMatch = line.match(/^(\s*)"([^"]+)":\s*(.+?)$/);
        if (kvMatch) {
          const [, indent, key, rawValue] = kvMatch;
          let valueEl: React.ReactNode;
          const value = rawValue.replace(/,$/, '');
          const comma = rawValue.endsWith(',') ? ',' : '';

          if (value.startsWith('"') && value.endsWith('"')) {
            // String value
            valueEl = <span className="text-emerald-400">{value}</span>;
          } else if (!isNaN(Number(value))) {
            // Number value
            valueEl = <span className="text-amber-400">{value}</span>;
          } else {
            valueEl = <span className="text-slate-300">{value}</span>;
          }

          return (
            <div key={i}>
              <span className="text-slate-500">{indent}</span>
              <span className="text-sky-400">&quot;{key}&quot;</span>
              <span className="text-slate-400">: </span>
              {valueEl}
              <span className="text-slate-500">{comma}</span>
            </div>
          );
        }

        // Braces / brackets
        return (
          <div key={i}>
            <span className="text-slate-400">{line}</span>
          </div>
        );
      })}
    </pre>
  );
}

/* ------------------------------------------------------------------ */
/*  Format badge                                                       */
/* ------------------------------------------------------------------ */
function FormatBadge({ format }: { format: string }) {
  const colorMap: Record<string, string> = {
    UUID: 'bg-violet-100 text-violet-700',
    String: 'bg-slate-100 text-slate-600',
    'Integer (cents)': 'bg-sky-100 text-sky-700',
    Float: 'bg-cyan-100 text-cyan-700',
    Decimal: 'bg-cyan-100 text-cyan-700',
    Enum: 'bg-amber-100 text-amber-700',
    'ISO 8601': 'bg-rose-100 text-rose-700',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded text-xs font-semibold', colorMap[format] ?? 'bg-slate-100 text-slate-600')}>
      {format}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Page component                                                     */
/* ------------------------------------------------------------------ */
function OutboundMappingsPage() {
  const [activeProcessor, setActiveProcessor] = useState<DisputeProcessor>('justt');
  const { data: allOutbound = [], isError: outboundError } = useOutboundMappings();

  const config = allOutbound.find((m) => m.processor === activeProcessor);
  const mappings = config?.mappings ?? [];

  return (
    <div className="flex-1 overflow-y-auto bg-[#f8f6f6] p-8">
      {outboundError && <DataLoadError what="outbound mappings" />}
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-3xl font-black text-slate-900">Outbound Mappings</h2>
          <p className="text-sm text-slate-500 mt-1">
            Canonical schema &rarr; dispute processor payload format. Controls how enrichment data is serialized for submission.
          </p>
        </div>
        <button className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors flex items-center gap-1.5">
          <Plus size={16} weight="regular" />
          Create New Mapping
        </button>
      </div>

      {/* Processor Tabs */}
      <div className="flex gap-0 border-b border-slate-200 mb-6">
        {PROCESSORS.map((p) => (
          <button
            key={p.key}
            onClick={() => setActiveProcessor(p.key)}
            className={cn(
              'px-5 py-2.5 text-sm font-semibold transition-colors -mb-px',
              activeProcessor === p.key
                ? 'border-b-2 border-slate-900 text-slate-900'
                : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Mappings Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-8">
        {mappings.length === 0 ? (
          <div className="px-8 py-16 text-center text-slate-400 text-sm">
            <Export size={32} weight="regular" className="mb-2 mx-auto" />
            No outbound mappings configured for {PROCESSOR_LABELS[activeProcessor]}.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Canonical Field</th>
                <th className="px-5 py-3">Target Field Name</th>
                <th className="px-5 py-3">Format</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {mappings.map((m) => (
                <tr key={m.canonicalField} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-3 font-medium text-slate-800">{m.canonicalField}</td>
                  <td className="px-5 py-3">
                    {m.targetField ? (
                      <code className="font-mono text-[13px] bg-slate-50/50 border border-slate-100 px-2 py-0.5 rounded text-slate-700">
                        {m.targetField}
                      </code>
                    ) : (
                      <span className="text-slate-400 italic text-xs">Not mapped</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <FormatBadge format={m.format} />
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge variant={m.status} />
                  </td>
                  <td className="px-5 py-3 text-right">
                    {m.status === 'mapped' ? (
                      <button className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                        <PencilSimple size={18} weight="regular" />
                      </button>
                    ) : (
                      <button className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                        Map Field
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BracketsCurly size={18} weight="regular" className="text-slate-400" />
            <h3 className="text-sm font-bold text-slate-900">
              Preview: Sample Mapped Payload
            </h3>
          </div>
          <span className="text-xs text-slate-400 font-medium">
            {PROCESSOR_LABELS[activeProcessor]} format
          </span>
        </div>
        <div className="bg-[#1E293B] p-6 overflow-x-auto">
          <SyntaxHighlightedJson data={SAMPLE_PAYLOADS[activeProcessor]} />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/schema/outbound')({
  component: OutboundMappingsPage,
});
